<template>
  <div class="notification-bell">
    <button type="button" class="bell-btn" @click.stop="toggle">
      <i class="fa fa-bell-o fa-lg" aria-hidden="true"></i>
      <!-- 未讀數超過 99 就顯示 99+，避免數字撐爆徽章 -->
      <span v-if="unreadCount > 0" class="bell-badge">{{ badgeText }}</span>
    </button>

    <div v-if="open" class="bell-panel" @click.stop>
      <div class="bell-panel-header">
        <span class="font-weight-500">我的通知</span>
        <a v-if="unreadCount > 0" href="javascript:void(0)" class="mark-all" @click="markAllRead">全部已讀</a>
      </div>

      <div class="bell-panel-body">
        <div v-if="loading" class="bell-empty">載入中...</div>
        <div v-else-if="items.length === 0" class="bell-empty">目前沒有通知</div>
        <div v-else>
          <div v-for="item in items" :key="item.id"
               class="bell-item" :class="{'is-unread': !item.is_read}"
               @click="clickItem(item)">
            <p class="m-0 font-size-13 font-weight-500 text-custom-text bell-text">
              <span v-if="!item.is_read" class="unread-dot"></span>{{ item.title }}
            </p>
            <p class="m-0 font-size-12 text-custom-text bell-body mt-1">{{ item.body }}</p>
            <p class="m-0 font-size-11 text-custom-gray-2 mt-1">{{ formatDateOnly(item.created_at) }}</p>
          </div>
        </div>
      </div>

      <div v-if="pushSupported" class="bell-push">
        <template v-if="pushState === 'enabled'">
          <span class="bell-push-text">推播通知：已開啟</span>
          <a href="javascript:void(0)" class="bell-push-link" @click="disablePush">關閉</a>
        </template>
        <template v-else-if="pushState === 'denied'">
          <span class="bell-push-text">瀏覽器已封鎖通知，請到網站設定開啟</span>
        </template>
        <template v-else>
          <a href="javascript:void(0)" class="bell-push-link" :class="{'is-busy': pushBusy}" @click="enablePush">
            開啟推播通知
          </a>
        </template>
        <p v-if="pushError" class="bell-push-error">{{ pushError }}</p>
      </div>

      <div class="bell-panel-footer">
        <a href="javascript:void(0)" @click="goToList">查看通知列表</a>
      </div>
    </div>
  </div>
</template>

<script>
import {formatDateOnly} from "@/util/formatter";
import * as Api from "@/util/http_api";
import * as push from "@/util/push";

export default {
  name: "NotificationBell",
  data() {
    return {
      open: false,
      loading: false,
      unreadCount: 0,
      items: [],
      // 下拉只是預覽，看完整清單請到通知頁
      previewLimit: 5,
      pollTimer: null,
      pollIntervalMs: 60000,
      // 網頁推播
      pushSupported: push.isSupported(),
      pushState: 'idle', // idle 還沒開、enabled 已開、denied 被瀏覽器封鎖
      pushBusy: false,
      pushError: '',
      pushSynced: false,
    };
  },
  computed: {
    badgeText() {
      return this.unreadCount > 99 ? '99+' : String(this.unreadCount);
    },
  },
  created() {
    this.refreshPushState();
    // 點了推播通知、網站已經開著時，service worker 會傳訊息過來要求導到對應頁面
    push.listenNavigate((path) => this.$router.push('/' + String(path).replace(/^\/+/, '')).catch(() => {}));
    this.refreshUnreadCount();
    // 其他頁面把通知讀掉時，鈴鐺的數字要跟著更新
    this.$root.$on('notifications-updated', this.refreshUnreadCount);
    // header 在切換路由時會被重用（created 只跑一次），所以另外定時重抓，
    // 否則使用者停在同一頁時，新進來的通知不會反映在徽章上，要整頁重新整理才看得到
    this.pollTimer = setInterval(this.refreshUnreadCount, this.pollIntervalMs);
  },
  mounted() {
    document.addEventListener('click', this.close);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.close);
    this.$root.$off('notifications-updated', this.refreshUnreadCount);
    clearInterval(this.pollTimer);
  },
  watch: {
    // 換頁時把面板收起來，順便重抓一次未讀數
    '$route'() {
      this.open = false;
      this.refreshUnreadCount();
    },
  },
  methods: {
    formatDateOnly,
    close() {
      this.open = false;
    },
    refreshPushState() {
      if (!this.pushSupported) return;
      const p = push.permission();
      this.pushState = p === 'denied' ? 'denied' : (push.isEnabled() ? 'enabled' : 'idle');
    },
    // 已經允許過通知的瀏覽器，登入後登記一次；頁面在前景時收到推播就更新鈴鐺
    async syncPushOnce() {
      if (this.pushSynced || !this.pushSupported || push.permission() !== 'granted') return;
      this.pushSynced = true;
      if (await push.syncIfGranted()) {
        this.refreshPushState();
        push.onForegroundMessage(() => {
          this.refreshUnreadCount();
          this.$root.$emit('notifications-updated');
          if (this.open) this.loadPreview();
        });
      }
    },
    async enablePush() {
      if (this.pushBusy) return;
      this.pushBusy = true;
      this.pushError = '';
      try {
        const result = await push.enable();
        if (result.ok) {
          this.pushSynced = false;
          this.pushState = 'enabled';
          this.syncPushOnce();
        } else if (result.reason === 'denied') {
          this.pushState = 'denied';
        } else if (result.reason !== 'default') {
          this.pushError = '開啟失敗，請稍後再試';
        }
      } finally {
        this.pushBusy = false;
        this.refreshPushState();
      }
    },
    async disablePush() {
      this.pushBusy = true;
      try {
        await push.disable();
      } finally {
        this.pushBusy = false;
        this.pushState = 'idle';
      }
    },
    toggle() {
      this.open = !this.open;
      if (this.open) this.loadPreview();
    },
    async refreshUnreadCount() {
      try {
        this.unreadCount = await Api.getUnreadNotificationCount();
        this.syncPushOnce();
      } catch (e) {
        // 未登入或 token 過期時會失敗，鈴鐺不顯示數字即可，不用打擾使用者
        this.unreadCount = 0;
      }
    },
    async loadPreview() {
      this.loading = true;
      try {
        const result = await Api.getNotifications(this.previewLimit, 0);
        this.items = result.items || [];
      } catch (e) {
        this.items = [];
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    async markAllRead() {
      try {
        await Api.markNotificationsRead();
        this.items = this.items.map(i => ({...i, is_read: true}));
        this.unreadCount = 0;
        this.$root.$emit('notifications-updated');
      } catch (e) {
        console.error(e);
      }
    },
    async clickItem(item) {
      if (!item.is_read) {
        try {
          await Api.markNotificationRead(item.id);
          item.is_read = true;
          this.refreshUnreadCount();
          this.$root.$emit('notifications-updated');
        } catch (e) {
          console.error(e);
        }
      }
      this.open = false;
      if (Number(item.type) === 2) {
        // 帳款通知：去帳單頁
        this.$router.push({name: 'customer_bills'}).catch(() => {});
      } else if (item.catalog_id) {
        this.$router.push({name: 'commodity', params: {id: item.catalog_id}}).catch(() => {});
      }
    },
    goToList() {
      this.open = false;
      this.$router.push({name: 'notifications'}).catch(() => {});
    },
  },
}
</script>

<style scoped>
.notification-bell {
  position: relative;
}

.bell-btn {
  position: relative;
  border: 0;
  background: transparent;
  color: #F39800;
  padding: 6px 10px;
  cursor: pointer;
}

.bell-badge {
  position: absolute;
  top: 0;
  right: 2px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 9px;
  background: #f5365c;
  color: #fff;
  font-size: 11px;
  line-height: 18px;
  text-align: center;
}

.bell-panel {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 1050;
  width: 320px;
  background: #fff;
  border: 1px solid #e3e6ea;
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}

.bell-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-bottom: 1px solid #f0f2f5;
}

.mark-all {
  font-size: 12px;
  color: #F39800;
}

.bell-panel-body {
  max-height: 360px;
  overflow-y: auto;
}

.bell-empty {
  padding: 28px 14px;
  text-align: center;
  color: #8898aa;
  font-size: 13px;
}

.bell-item {
  padding: 10px 14px;
  border-bottom: 1px solid #f6f9fc;
  cursor: pointer;
}

.bell-item:hover {
  background: #f6f9fc;
}

.bell-item.is-unread {
  background: rgba(243, 152, 0, 0.06);
}

.bell-text {
  white-space: normal;
  word-break: break-word;
}

/* 鈴鐺面板只顯示前三行，完整內容到通知頁看 */
.bell-body {
  white-space: pre-line;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.unread-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #E89100;
  margin-right: 6px;
}

.bell-push {
  padding: 8px 14px;
  text-align: center;
  border-top: 1px solid #f0f2f5;
  font-size: 12px;
}

.bell-push-text {
  color: #8898aa;
  margin-right: 8px;
}

.bell-push-link {
  color: #F39800;
}

.bell-push-link.is-busy {
  opacity: 0.5;
  pointer-events: none;
}

.bell-push-error {
  margin: 4px 0 0;
  color: #f5365c;
}

.bell-panel-footer {
  padding: 10px 14px;
  text-align: center;
  border-top: 1px solid #f0f2f5;
}

.bell-panel-footer a {
  color: #F39800;
  font-size: 13px;
}
</style>
